// src/pages/rankSummary/MyMarkets.js
import React, { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import Table from "components/UnifiedMarkupTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMedal, faPoo } from "@fortawesome/free-solid-svg-icons";
import { fetchMyMarkets } from "./api/index";
import SupplierLogo from "./SupplierLogo";
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import SortUpIcon from '@enerex/unified-ui-react/icons/SortUpIcon';
import SortDownIcon from '@enerex/unified-ui-react/icons/SortDownIcon';
import loader from "assets/images/datahub-loader.svg";
import { getStateAbbreviation } from "./utils/stateAbbreviations";
import "./RankSummary.css";

const electricityIconStyle = { color: "#ffc107" };
const gasIconStyle = { color: "#0acf97" };

const MyMarkets = () => {
  const [loading, setLoading] = useState(true);
  const [markets, setMarkets] = useState([]);
  const [error, setError] = useState(null);
  const [effectiveDate, setEffectiveDate] = useState(null);
  const [contractStartDate, setContractStartDate] = useState(null);
  
  // Sorting state
  const [electricSortConfig, setElectricSortConfig] = useState({ 
    field: 'state', 
    direction: 'asc' 
  });
  const [gasSortConfig, setGasSortConfig] = useState({ 
    field: 'state', 
    direction: 'asc' 
  });

  useEffect(() => {
    const loadMarkets = async () => {
      try {
        setLoading(true);

        // ✅ Call your API wrapper
        const data = await fetchMyMarkets("");

        // Extract dates from the response (should be same across all commodities)
        if (data && Array.isArray(data) && data.length > 0) {
          const firstBlock = data[0];
          if (firstBlock.EffectiveDate) {
            setEffectiveDate(firstBlock.EffectiveDate);
          }
          if (firstBlock.ContractStartDate) {
            setContractStartDate(firstBlock.ContractStartDate);
          }
        } else if (data && !Array.isArray(data)) {
          // Handle case where response is a single object
          if (data.EffectiveDate) {
            setEffectiveDate(data.EffectiveDate);
          }
          if (data.ContractStartDate) {
            setContractStartDate(data.ContractStartDate);
          }
        }

        // Flatten both commodities into one array
        const allMarkets = (Array.isArray(data) ? data : [data]).flatMap((block) =>
          (block?.Rankings || []).map((r) => ({
            ...r,
            Commodity: block.Commodity,
            Date: block.Date,
          }))
        );

        setMarkets(allMarkets);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadMarkets();
  }, []);

if (loading)
  return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <img
        src={loader}
        alt="Loading..."
        style={{ width: "80px", height: "80px" }}
      />
    </div>
  );


  if (error) return <div className="alert alert-danger">{error}</div>;

  const allMarkets = markets;

  // Split by commodity for side-by-side tables
  const electricMarkets = allMarkets.filter(
    (m) => (m.Commodity || "").toString() === "1"
  );
  const gasMarkets = allMarkets.filter(
    (m) => (m.Commodity || "").toString() !== "1"
  );

  const renderRankingCell = (supplierRank) => {
    if (!supplierRank || supplierRank.IsLive === false) return <td style={{ textAlign: "center", verticalAlign: "middle", padding: "8px" }}>-</td>;
    
    return (
      <td style={{ textAlign: "center", verticalAlign: "top", padding: "8px" }}>
        <div className="d-flex flex-column align-items-center">
          <div className="logo-frame" style={{ 
            width: "50px", 
            height: "50px", 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center",
            border: "1px solid #e5e5e5",
            borderRadius: "6px",
            backgroundColor: "#fff",
            paddingTop: "2px"
          }}>
            <SupplierLogo supplierId={supplierRank.SupplierEnerexIdentifier} supplierName={supplierRank.SupplierName} size={35} />
          </div>
          {supplierRank.SupplierName && (
            <div style={{ 
              fontSize: "10px", 
              color: "#474d56", 
              marginTop: "4px",
              textAlign: "center",
              wordWrap: "break-word",
              lineHeight: "1.2"
            }}>
              {supplierRank.SupplierName}
            </div>
          )}
        </div>
      </td>
    );
  };

  if (allMarkets.length === 0) {
    return (
      <div className="p-3">
        <p className="text-muted">No markets available.</p>
      </div>
    );
  }

  // Generic sorting function
  const sortMarkets = (list, sortConfig) => {
    const copy = [...list];
    return copy.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.field === 'state') {
        // Sort by state abbreviation instead of full state name
        aValue = getStateAbbreviation(a.State?.trim() || "").toLowerCase();
        bValue = getStateAbbreviation(b.State?.trim() || "").toLowerCase();
      } else if (sortConfig.field === 'utility') {
        aValue = (a.Utility?.trim() || "").toLowerCase();
        bValue = (b.Utility?.trim() || "").toLowerCase();
      }
      
      let comparison = aValue.localeCompare(bValue);
      
      // If states are the same, sort by utility as secondary sort
      if (sortConfig.field === 'state' && comparison === 0) {
        const utilityA = (a.Utility?.trim() || "").toLowerCase();
        const utilityB = (b.Utility?.trim() || "").toLowerCase();
        comparison = utilityA.localeCompare(utilityB);
      }
      
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  };

  // Sort handlers
  const handleElectricSort = (field) => {
    const direction = electricSortConfig.field === field && electricSortConfig.direction === 'asc' 
      ? 'desc' 
      : 'asc';
    setElectricSortConfig({ field, direction });
  };

  const handleGasSort = (field) => {
    const direction = gasSortConfig.field === field && gasSortConfig.direction === 'asc' 
      ? 'desc' 
      : 'asc';
    setGasSortConfig({ field, direction });
  };

  // Get sort icon
  const getSortIcon = (field, sortConfig) => {
    return (
      <div className="d-flex flex-column" style={{ width: 'fit-content' }}>
        <SortUpIcon
          className={sortConfig.field === field && sortConfig.direction === 'asc' ? 'text-primary' : 'text-secondary'}
        />
        <SortDownIcon
          className={sortConfig.field === field && sortConfig.direction === 'desc' ? 'text-primary' : 'text-secondary'}
          style={{ marginTop: '-10px' }}
        />
      </div>
    );
  };

  const sortedElectricMarkets = sortMarkets(electricMarkets, electricSortConfig);
  const sortedGasMarkets = sortMarkets(gasMarkets, gasSortConfig);

  // Format ContractStartDate from "2026-04-01" to "April 2026"
  const formatContractStartDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      const month = date.toLocaleDateString("en-US", { month: "long", timeZone: "UTC" });
      const year = date.getFullYear();
      return `${month} ${year}`;
    } catch {
      return dateString;
    }
  };

  return (
    <div className="p-3" style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
      {/* Informational Message */}
      {(effectiveDate || contractStartDate) && (
        <Alert 
          variant="info" 
          className="mb-4"
          style={{
            backgroundColor: "#e7f3ff",
            border: "1px solid #b3d9ff",
            borderRadius: "8px",
            padding: "12px 16px",
            fontSize: "14px",
            color: "#004085",
            marginBottom: "20px"
          }}
        >
          <strong>Note:</strong>{" "}
          Pricing is effective as of <strong>{effectiveDate || "N/A"}</strong>
          {contractStartDate && (
            <>
              {" "}
              and is applicable to the <strong>{formatContractStartDate(contractStartDate)}</strong> start date.
            </>
          )}
          {!contractStartDate && "."}{" "}
          The product type is <strong>fixed</strong> and{" "}
          <strong>all-inclusive</strong>, with a{" "}
          <strong>12 month</strong> contractual term.
        </Alert>
      )}
      
      <div className="row g-3">
        {/* Left: Electric */}
        <div className="col-12 col-lg-6 mb-4 mb-lg-0">
          <div className="mb-3 d-flex justify-content-center">
            <ElectricityIcon className="commodity-icon" style={{ ...electricityIconStyle, width: 32, height: 32 }} />
          </div>
          <div className="table-responsive">
            <Table hover className="table-centered" style={{ backgroundColor: "#fafbfe", border: "1px solid #dee2e6", tableLayout: "fixed", width: "100%" }}>
              <thead style={{ backgroundColor: "#eef2f7" }}>
                 <tr>
                   <th 
                     style={{ width: "15%", padding: "8px", textAlign: "center", fontSize: "12px", fontWeight: "900", color: "#474d56", textTransform: "uppercase", cursor: "pointer", userSelect: "none", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}
                     onClick={() => handleElectricSort('state')}
                   >
                     <div className="d-flex align-items-center justify-content-center gap-1">
                       <span>State</span>
                       {getSortIcon('state', electricSortConfig)}
                     </div>
                   </th>
                    <th 
                      style={{ width: "18%", padding: "8px", textAlign: "center", fontSize: "12px", fontWeight: "900", color: "#474d56", textTransform: "uppercase", cursor: "pointer", userSelect: "none", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}
                      onClick={() => handleElectricSort('utility')}
                    >
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <span>Utility</span>
                        {getSortIcon('utility', electricSortConfig)}
                      </div>
                    </th>
                    <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                      <div className="d-flex flex-column align-items-center gap-1">
                        <FontAwesomeIcon icon={faMedal} className="medal-icon medal-gold" style={{ fontSize: "18px" }} />
                        <span>1st</span>
                      </div>
                    </th>
                    <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                      <div className="d-flex flex-column align-items-center gap-1">
                        <FontAwesomeIcon icon={faMedal} className="medal-icon medal-silver" style={{ fontSize: "18px" }} />
                        <span>2nd</span>
                      </div>
                    </th>
                    <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                      <div className="d-flex flex-column align-items-center gap-1">
                        <FontAwesomeIcon icon={faMedal} className="medal-icon medal-bronze" style={{ fontSize: "18px" }} />
                        <span>3rd</span>
                      </div>
                    </th>
                    <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                      <div className="d-flex flex-column align-items-center gap-1">
                        <FontAwesomeIcon icon={faPoo} className="poo-icon" style={{ fontSize: "18px" }} />
                        <span>Worst</span>
                      </div>
                    </th>
                 </tr>
              </thead>
              <tbody style={{ backgroundColor: "#ffffff" }}>
                 {sortedElectricMarkets.length === 0 ? (
                   <tr>
                     <td colSpan={6} style={{ textAlign: "center", padding: "16px", fontSize: "12px", color: "#8898aa" }}>
                       No electric markets available.
                     </td>
                   </tr>
                ) : (
                   sortedElectricMarkets.map((market, idx) => {
                      // Get top 3 suppliers
                      const top3Suppliers = [
                        market.SupplierRank[0],
                        market.SupplierRank[1],
                        market.SupplierRank[2]
                      ].filter(Boolean);
                      
                      // Get worst supplier (last one)
                      const worstSupplier = market.SupplierRank.length > 0 
                        ? market.SupplierRank[market.SupplierRank.length - 1] 
                        : null;
                      
                      // Check if worst supplier is already in top 3
                      const isWorstInTop3 = worstSupplier && top3Suppliers.some(
                        (supplier) => supplier.SupplierEnerexIdentifier === worstSupplier.SupplierEnerexIdentifier
                      );
                      
                       return (
                         <tr key={`${market.State}-${market.Utility}-${idx}`} style={{ borderBottom: "2px solid rgba(152, 166, 173, 0.2)" }}>
                         <td style={{ verticalAlign: "middle", padding: "8px", textAlign: "center", fontSize: "12px", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                           <strong style={{ fontWeight: "900" }}>
                             {getStateAbbreviation(market.State)}
                           </strong>
                         </td>
                         <td style={{ verticalAlign: "middle", padding: "8px", textAlign: "center", fontSize: "12px", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                           <strong style={{ fontWeight: "900" }}>{market.Utility?.trim()}</strong>
                          </td>
                        {renderRankingCell(market.SupplierRank[0])}
                        {renderRankingCell(market.SupplierRank[1])}
                        {renderRankingCell(market.SupplierRank[2])}
                        {renderRankingCell(isWorstInTop3 ? null : worstSupplier)}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Right: Gas */}
        <div className="col-12 col-lg-6">
          <div className="mb-3 d-flex justify-content-center">
            <GasIcon className="commodity-icon" style={{ ...gasIconStyle, width: 32, height: 32 }} />
          </div>
          <div className="table-responsive">
            <Table hover className="table-centered" style={{ backgroundColor: "#fafbfe", border: "1px solid #dee2e6", tableLayout: "fixed", width: "100%" }}>
              <thead style={{ backgroundColor: "#eef2f7" }}>
                 <tr>
                   <th 
                     style={{ width: "15%", padding: "8px", textAlign: "center", fontSize: "12px", fontWeight: "900", color: "#474d56", textTransform: "uppercase", cursor: "pointer", userSelect: "none", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}
                     onClick={() => handleGasSort('state')}
                   >
                     <div className="d-flex align-items-center justify-content-center gap-1">
                       <span>State</span>
                       {getSortIcon('state', gasSortConfig)}
                     </div>
                   </th>
                    <th 
                      style={{ width: "18%", padding: "8px", textAlign: "center", fontSize: "12px", fontWeight: "900", color: "#474d56", textTransform: "uppercase", cursor: "pointer", userSelect: "none", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}
                      onClick={() => handleGasSort('utility')}
                    >
                      <div className="d-flex align-items-center justify-content-center gap-1">
                        <span>Utility</span>
                        {getSortIcon('utility', gasSortConfig)}
                      </div>
                    </th>
                     <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                       <div className="d-flex flex-column align-items-center gap-1">
                         <FontAwesomeIcon icon={faMedal} className="medal-icon medal-gold" style={{ fontSize: "18px" }} />
                         <span>1st</span>
                       </div>
                     </th>
                    <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                      <div className="d-flex flex-column align-items-center gap-1">
                        <FontAwesomeIcon icon={faMedal} className="medal-icon medal-silver" style={{ fontSize: "18px" }} />
                        <span>2nd</span>
                      </div>
                    </th>
                    <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                      <div className="d-flex flex-column align-items-center gap-1">
                        <FontAwesomeIcon icon={faMedal} className="medal-icon medal-bronze" style={{ fontSize: "18px" }} />
                        <span>3rd</span>
                      </div>
                    </th>
                    <th style={{ width: "17%", textAlign: "center", padding: "8px", fontSize: "12px", fontWeight: "600", color: "#474d56" }}>
                      <div className="d-flex flex-column align-items-center gap-1">
                        <FontAwesomeIcon icon={faPoo} className="poo-icon" style={{ fontSize: "18px" }} />
                        <span>Worst</span>
                      </div>
                    </th>
                </tr>
              </thead>
              <tbody style={{ backgroundColor: "#ffffff" }}>
                {sortedGasMarkets.length === 0 ? (
                  <tr>
                     <td colSpan={6} style={{ textAlign: "center", padding: "16px", fontSize: "12px", color: "#8898aa" }}>
                      No gas markets available.
                    </td>
                  </tr>
                ) : (
                  sortedGasMarkets.map((market, idx) => {
                     // Get top 3 suppliers
                     const top3Suppliers = [
                       market.SupplierRank[0],
                       market.SupplierRank[1],
                       market.SupplierRank[2]
                     ].filter(Boolean);
                     
                     // Get worst supplier (last one)
                     const worstSupplier = market.SupplierRank.length > 0 
                       ? market.SupplierRank[market.SupplierRank.length - 1] 
                       : null;
                     
                     // Check if worst supplier is already in top 3
                     const isWorstInTop3 = worstSupplier && top3Suppliers.some(
                       (supplier) => supplier.SupplierEnerexIdentifier === worstSupplier.SupplierEnerexIdentifier
                     );
                     
                       return (
                         <tr key={`${market.State}-${market.Utility}-${idx}`} style={{ borderBottom: "2px solid rgba(152, 166, 173, 0.2)" }}>
                         <td style={{ verticalAlign: "middle", padding: "8px", textAlign: "center", fontSize: "12px", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                           <strong style={{ fontWeight: "900" }}>
                             {getStateAbbreviation(market.State)}
                           </strong>
                         </td>
                         <td style={{ verticalAlign: "middle", padding: "8px", textAlign: "center", fontSize: "12px", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "anywhere" }}>
                           <strong style={{ fontWeight: "900" }}>{market.Utility?.trim()}</strong>
                           </td>
                        {renderRankingCell(market.SupplierRank[0])}
                        {renderRankingCell(market.SupplierRank[1])}
                        {renderRankingCell(market.SupplierRank[2])}
                        {renderRankingCell(isWorstInTop3 ? null : worstSupplier)}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyMarkets;
